/*
 * Logistics Collaboration Dashboard — Status & Action map
 * Single source of truth for the status pill colour, label, and the
 * sponsor-facing action that becomes available on each status.
 *
 * Status IDs are numeric DB values (object_type = 'Shipment').
 *
 * Flow: Shipment Authorized → Shipment Scheduled → (Sync Status) → Shipped
 *       → (Sync Status) → Goods Received → Consignee GRN Generated.
 *
 * View placement + action button rules:
 *   Pre-Schedule View   – Shipment Authorized (62) only.
 *                          • Shipment Authorized        → "Schedule Shipment"
 *   Post-Schedule View  – Shipment Scheduled (63) only.
 *                          • Shipment Scheduled         → "Choose the action" (menu has "Sync Status" → Shipped)
 *   Post-Shipment View  – Shipped (64), Proforma (65-68), Goods Received (59), Consignee GRN Generated (69).
 *                          • Shipped                    → "Choose the action" (menu has "Sync Status" → Goods Received)
 *                          • Goods Received             → "Generate Consignee GRN"
 *                          • every other status         → "Choose the action"
 */

export const STATUS_COLORS = {
  purple:  { bg: '#7E2768', fg: '#FFFFFF' }, // Proforma Uploaded
  teal:    { bg: '#188E84', fg: '#FFFFFF' }, // Proforma Digitalized
  navy:    { bg: '#24557F', fg: '#FFFFFF' }, // Shipment Authorized / Proforma Sent
  green:   { bg: '#277F24', fg: '#FFFFFF' }, // Shipped / Goods Received / Consignee GRN Generated
  olive:   { bg: '#617F24', fg: '#FFFFFF' }, // Shipment Scheduled / Proforma Requested
  red:     { bg: '#A02828', fg: '#FFFFFF' }, // Delayed / error overlays
  neutral: { bg: '#E0E0E0', fg: '#454A5C' },
};

export const VIEWS = {
  PRE_SCHEDULE:  'preSchedule',
  POST_SCHEDULE: 'postSchedule',
  POST_DELIVERY: 'postDelivery',
};

export const ACTIONS = {
  REVIEW_DOCUMENT:        'REVIEW_DOCUMENT',
  CHOOSE_ACTION:          'CHOOSE_ACTION',
  SELECT_BATCHES:         'SELECT_BATCHES',
  GENERATE_PROFORMA:      'GENERATE_PROFORMA',
  SHARE_DOCUMENT:         'SHARE_DOCUMENT',
  SEND_ASN:               'SEND_ASN',
  GOODS_RECEIVED:         'GOODS_RECEIVED',
  RESEND:                 'RESEND',
  INVESTIGATE:            'INVESTIGATE',
  SCHEDULE_SHIPMENT:      'SCHEDULE_SHIPMENT',
  GENERATE_DOCUMENT:      'GENERATE_DOCUMENT',
  GENERATE_CONSIGNEE_GRN: 'GENERATE_CONSIGNEE_GRN',
  SYNC_STATUS:            'SYNC_STATUS',
};

const choose = {
  sponsorAction:      ACTIONS.CHOOSE_ACTION,
  sponsorActionLabel: 'Choose the action',
  actionStyle:        'choose',
};

// Keyed by numeric DB status_id (object_type = 'Shipment')
export const STATUSES = {
  // ── Pre-Schedule View ──────────────────────────────────────────────────────
  62: {
    id: 62,
    label: 'Shipment Authorized',
    color: 'navy',
    view: VIEWS.PRE_SCHEDULE,
    sponsorAction:      ACTIONS.SCHEDULE_SHIPMENT,
    sponsorActionLabel: 'Schedule Shipment',
    actionStyle:        'outline',
    enabled: true,
  },

  // ── Post-Schedule View ────────────────────────────────────────────────────
  63: {
    id: 63,
    label: 'Shipment Scheduled',
    color: 'olive',
    view: VIEWS.POST_SCHEDULE,
    ...choose,
    enabled: true,
  },
  64: {
    id: 64,
    label: 'Shipped',
    color: 'green',
    view: VIEWS.POST_DELIVERY,
    sponsorAction:      ACTIONS.GENERATE_PROFORMA,
    sponsorActionLabel: 'Generate Proforma',
    actionStyle:        'outline',
    enabled: true,
  },
  65: {
    id: 65,
    label: 'Proforma Requested',
    color: 'olive',
    view: VIEWS.POST_DELIVERY,
    ...choose,
    enabled: true,
  },
  66: {
    id: 66,
    label: 'Proforma Uploaded',
    color: 'purple',
    view: VIEWS.POST_DELIVERY,
    // sponsorAction:      ACTIONS.REVIEW_DOCUMENT,
    // sponsorActionLabel: 'Review Document',
    // actionStyle:        'outline',
    ...choose,
    enabled: true,
  },
  67: {
    id: 67,
    label: 'Proforma Digitalized',
    color: 'teal',
    view: VIEWS.POST_DELIVERY,
    ...choose,
    enabled: true,
  },
  68: {
    id: 68,
    label: 'Proforma Sent',
    color: 'navy',
    view: VIEWS.POST_DELIVERY,
    ...choose,
    enabled: true,
  },

  // ── Post-Delivery View ─────────────────────────────────────────────────────
  59: {
    id: 59,
    label: 'Goods Received',
    color: 'green',
    view: VIEWS.POST_DELIVERY,
    sponsorAction:      ACTIONS.GENERATE_CONSIGNEE_GRN,
    sponsorActionLabel: 'Generate Consignee GRN',
    actionStyle:        'outline',
    enabled: true,
  },
  69: {
    id: 69,
    label: 'Consignee GRN Generated',
    color: 'green',
    view: VIEWS.POST_DELIVERY,
    ...choose,
    enabled: true,
  },
};

// Statuses visible in each view (mirrors the Corda buildSelectedDashboardViewClause).
// Post-Schedule shows ONLY Shipment Scheduled (63); everything from Shipped onward is
// in the Post-Shipment view.
export const STATUSES_BY_VIEW = {
  [VIEWS.PRE_SCHEDULE]:  [62],
  [VIEWS.POST_SCHEDULE]: [63],
  [VIEWS.POST_DELIVERY]: [64, 65, 66, 67, 68, 59, 69],
};

export const OVERLAY_STATUSES = [];

// KPI tiles
export const QUICK_SUMMARY_TILES = [
  { key: 'marketPOsReady',     label: 'Market POs ready for dispatch', danger: false },
  { key: 'marketPOsScheduled', label: 'Market POs scheduled',          danger: false },
  { key: 'marketPOsInTransit', label: 'Market POs in transit',         danger: false },
  { key: 'marketPOsDelivered', label: 'Market POs delivered',          danger: false },
  { key: 'newActions',         label: 'Deliveries with new action',    danger: false },
  { key: 'completed',          label: 'Deliveries completed',          danger: false },
  { key: 'delayed',            label: 'Delayed deliveries',            danger: true  },
];

export const SEARCH_CRITERIA = [
  { key: 'all',                label: 'Select criteria' },
  { key: 'poNumber',           label: 'PO Number' },
  { key: 'productDescription', label: 'Product Description' },
  { key: 'shipmentStatus',     label: 'Shipment Status' },
  { key: 'batchNumber',        label: 'Batch Number' },
  // { key: 'shipmentScheduleId', label: 'Shipment Schedule ID' },
  { key: 'plNumber',           label: 'PL Number' },
  { key: 'Country',            label: 'Country' },
];

export const LIST_OF_ACTIONS = [
  { key: 'all',                              label: 'List all shipments' },
  // { key: 'shipmentSchedulePending',       label: 'View shipments with scheduling pending' },
  { key: 'shipmentsWithPendingProformaGeneration', label: 'View shipments with pending proforma generation' },
  { key: 'shipmentDelayed',                 label: 'View shipments delayed' },
  { key: 'shipmentConsigneeGRNPending',     label: 'View shipments with pending consignee GRN Generation' },
];

export const TILE_TO_LIST_OF_ACTIONS = {
  marketPOsReady:     'readyForDispatchPOs',
  marketPOsScheduled: 'shipmentScheduledPOs',
  marketPOsInTransit: 'shipmentInTransitPOs',
  marketPOsDelivered: 'shipmentDeliveredPOs',
  newActions:         'deliveriesWithNewAction',
  completed:          'deliveriesCompleted',
  delayed:            'delayedDeliveries',
};

export const VIEW_OPTIONS = [
  { key: VIEWS.PRE_SCHEDULE,  label: 'Pre-Schedule View'  },
  { key: VIEWS.POST_SCHEDULE, label: 'Post-Schedule View' },
  { key: VIEWS.POST_DELIVERY, label: 'Post-Shipment View' },
];

// ─── Sync Status transitions ──────────────────────────────────────────────────
// The "Sync Status" action ingests a freight-forwarder document and advances a
// shipment by exactly one step. Keyed by the shipment's CURRENT statusID:
//   • 63 Shipment Scheduled → 64 Shipped            (Post-Schedule view — NEW)
//   • 64 Shipped            → 59 Goods Received      (Post-Shipment view — existing)
// Both the SyncShipmentStatusDialog (success-screen labels/event) and the
// dashboard (client-side status update after ingest) read this map.
export const SYNC_TRANSITIONS = {
  63: {
    fromLabel:   'Shipment Scheduled',
    toStatusId:  64,
    toLabel:     'Shipped',
    eventTitle:  'Shipment Dispatched',
  },
  64: {
    fromLabel:   'Shipped',
    toStatusId:  59,
    toLabel:     'Goods Received',
    eventTitle:  'Goods Delivered at Consignee',
  },
};

export const getRowAction = (statusId, view) => {
  const s = STATUSES[statusId];
  if (!s) return { label: 'Choose the action', enabled: true, action: ACTIONS.CHOOSE_ACTION, style: 'choose' };
  // Goods Received in Post-Schedule view shows "Choose the action", not "Generate Consignee GRN"
  if (statusId === 59 && view === VIEWS.POST_SCHEDULE) {
    return { label: 'Choose the action', enabled: true, action: ACTIONS.CHOOSE_ACTION, style: 'choose' };
  }
  // Shipped in Post-Shipment (Post-Delivery) view shows a neutral "Choose the action";
  // the Sync Status action lives inside its dropdown menu (see RowActionButton).
  if (statusId === 64 && view === VIEWS.POST_DELIVERY) {
    return { label: 'Choose the action', enabled: true, action: ACTIONS.CHOOSE_ACTION, style: 'choose' };
  }
  return {
    label:   s.sponsorActionLabel,
    enabled: s.enabled,
    action:  s.sponsorAction,
    style:   s.actionStyle || 'choose',
  };
};

export const getStatusColor = (statusId) => {
  const s = STATUSES[statusId];
  if (!s) return STATUS_COLORS.neutral;
  return STATUS_COLORS[s.color] || STATUS_COLORS.neutral;
};
